/**
 * Membership gate plumbing — the "membership required" broadcast and the one rule
 * for when the client paywalls.
 *
 * Mirrors `lib/auth-token.ts`'s unauthorized pair: the API client dispatches an
 * event rather than importing the store, and `AuthGate` translates it into state.
 */

import type { AuthUser } from "~/types/auth";

/** Fired when the API answers 403 on an authenticated call; the member lost access. */
export const MEMBERSHIP_REQUIRED_EVENT = "elyxeer:membership-required";

/** Tell the app the member's subscription no longer covers the content. */
export function notifyMembershipRequired(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(MEMBERSHIP_REQUIRED_EVENT));
  }
}

/**
 * Whether the member should see the paywall instead of content.
 *
 * Access needs positive evidence: either a confirmed active subscription or the
 * administrator-granted app-only override. `"unknown"` is the pre-first-sync state,
 * not a temporary membership; otherwise a failed lookup would bypass the paywall.
 * A missing status from an older API fails closed for the same reason.
 */
export function isGated(user: AuthUser | null): boolean {
  return Boolean(
    user &&
    !user.has_membership_override &&
    user.membership_status !== "active",
  );
}
